import { addDays, subDays, format } from "date-fns";

// ─── Helpers ──────────────────────────────────────────────────────────────────
const d = (offset: number) => format(addDays(new Date(), offset), "yyyy-MM-dd");
const past = (offset: number) =>
  format(subDays(new Date(), offset), "yyyy-MM-dd");
const iso = (daysAgo: number) => subDays(new Date(), daysAgo).toISOString();

// ─── Mock UUIDs ───────────────────────────────────────────────────────────────
// Stable, readable UUIDs for mock data so tests/UI are predictable.
// User IDs:  00000000-0000-0000-0001-xxxxxxxxxxxx
// Item IDs:  00000000-0000-0000-0002-xxxxxxxxxxxx
// History:   00000000-0000-0000-0003-xxxxxxxxxxxx
const uid = (n: number) =>
  `00000000-0000-0000-0001-${String(n).padStart(12, "0")}`;
const iid = (n: number) =>
  `00000000-0000-0000-0002-${String(n).padStart(12, "0")}`;
const hid = (n: number) =>
  `00000000-0000-0000-0003-${String(n).padStart(12, "0")}`;

// ─── Mock Users ───────────────────────────────────────────────────────────────
export const MOCK_USERS = [
  {
    id: uid(1),
    email: "alice@company.com",
    name: "Alice Johnson",
    avatar: null,
  },
  { id: uid(2), email: "bob@company.com", name: "Bob Martinez", avatar: null },
  { id: uid(3), email: "carol@company.com", name: "Carol Smith", avatar: null },
  { id: uid(4), email: "david@company.com", name: "David Lee", avatar: null },
  { id: uid(5), email: "emily@company.com", name: "Emily Chen", avatar: null },
];

// ─── Mock Expiry Items ────────────────────────────────────────────────────────
// Ordered to show every action state upfront. Dates are always relative to
// today so statuses stay accurate regardless of when the app is opened.
export const MOCK_ITEMS = [
  // ── 🔴 EXPIRED → "Renew" action ───────────────────────────────────────────
  {
    id: iid(1),
    name: "Trade License",
    category: "Licenses & Permits",
    expiry_date: past(10), // expired 10 days ago
    quantity: 1,
    unit: "doc",
    notes: "Annual renewal required",
    owners: [MOCK_USERS[0], MOCK_USERS[1]],
    created_at: past(365),
    updated_at: past(10),
  },
  {
    id: iid(12),
    name: "Cloud Hosting Agreement",
    category: "Operations",
    expiry_date: past(3), // expired 3 days ago
    quantity: 1,
    unit: "contract",
    notes: "AWS enterprise agreement",
    owners: [MOCK_USERS[4]],
    created_at: past(365),
    updated_at: past(3),
  },

  // ── 🟠 HIGH RISK → "Send Mail" action ────────────────────────────────────
  {
    id: iid(5),
    name: "Fire Safety Certificate",
    category: "Compliance",
    expiry_date: d(4), // expires in 4 days
    quantity: 1,
    unit: "cert",
    notes: "Bi-annual inspection required",
    owners: [MOCK_USERS[0]],
    created_at: past(180),
    updated_at: past(1),
  },
  {
    id: iid(9),
    name: "Health & Safety Permit",
    category: "Compliance",
    expiry_date: d(6), // expires in 6 days
    quantity: 1,
    unit: "permit",
    notes: null,
    owners: [MOCK_USERS[0], MOCK_USERS[2]],
    created_at: past(365),
    updated_at: past(0),
  },

  // ── 🟡 UPCOMING → "Send Mail" action ─────────────────────────────────────
  {
    id: iid(2),
    name: "Insurance Policy",
    category: "Insurance",
    expiry_date: d(12), // expires in 12 days
    quantity: 1,
    unit: "policy",
    notes: "Comprehensive coverage",
    owners: [MOCK_USERS[2]],
    created_at: past(180),
    updated_at: past(5),
  },
  {
    id: iid(6),
    name: "Professional Indemnity Insurance",
    category: "Insurance",
    expiry_date: d(22), // expires in 22 days
    quantity: 1,
    unit: "policy",
    notes: null,
    owners: [MOCK_USERS[1], MOCK_USERS[4]],
    created_at: past(120),
    updated_at: past(3),
  },

  // ── ⚫ MISSING EXPIRY → "Add Fields" action ───────────────────────────────
  {
    id: iid(4),
    name: "Employee Visa",
    category: "HR",
    expiry_date: null, // no expiry date set
    quantity: null,
    unit: null,
    notes: "Requires HR review — expiry date unknown",
    owners: [],
    created_at: past(60),
    updated_at: past(1),
  },
  {
    id: iid(8),
    name: "Data Processing Agreement",
    category: "Legal",
    expiry_date: null, // no expiry date set
    quantity: null,
    unit: null,
    notes: "GDPR compliance doc — needs expiry date added",
    owners: [MOCK_USERS[2]],
    created_at: past(45),
    updated_at: past(45),
  },
  {
    id: iid(13),
    name: "Office Lease Addendum",
    category: "Real Estate",
    expiry_date: null, // no expiry date set
    quantity: null,
    unit: null,
    notes: "Addendum to main lease — date not recorded yet",
    owners: [MOCK_USERS[3]],
    created_at: past(20),
    updated_at: past(20),
  },

  // ── 🟢 ACTIVE → no urgent action (—) ─────────────────────────────────────
  {
    id: iid(3),
    name: "Vendor Contract",
    category: "Contracts",
    expiry_date: d(200), // expires in 200 days
    quantity: 1,
    unit: "contract",
    notes: "Software vendor agreement",
    owners: [MOCK_USERS[3]],
    created_at: past(90),
    updated_at: past(2),
  },
  {
    id: iid(7),
    name: "Equipment Lease Agreement",
    category: "Finance",
    expiry_date: d(180), // expires in 180 days
    quantity: 3,
    unit: "units",
    notes: "Server hardware lease",
    owners: [MOCK_USERS[4]],
    created_at: past(200),
    updated_at: past(7),
  },
  {
    id: iid(10),
    name: "SaaS Subscription – Figma",
    category: "Operations",
    expiry_date: d(95), // expires in 95 days
    quantity: 15,
    unit: "seats",
    notes: "Design tool subscription",
    owners: [MOCK_USERS[1]],
    created_at: past(270),
    updated_at: past(10),
  },
  {
    id: iid(11),
    name: "Building Lease",
    category: "Real Estate",
    expiry_date: d(365), // expires in 365 days
    quantity: 1,
    unit: "lease",
    notes: "Head office lease renewal next year",
    owners: [MOCK_USERS[3], MOCK_USERS[4]],
    created_at: past(500),
    updated_at: past(30),
  },
];

// ─── Mock History (keyed by item UUID) ───────────────────────────────────────
export const MOCK_HISTORY: Record<string, any[]> = {
  [iid(1)]: [
    {
      id: hid(101),
      type: "renewed",
      description: "License renewed for another year",
      date: iso(10),
      user: "Alice Johnson",
      meta: { previousDate: past(375), newDate: past(10) },
    },
    {
      id: hid(102),
      type: "reminder_sent",
      description: "Automated reminder email sent (7 days before expiry)",
      date: iso(17),
      user: "Automated",
      meta: { days_before: 7, sent_to: "alice@company.com" },
    },
    {
      id: hid(103),
      type: "edited",
      description: "Category updated",
      date: iso(90),
      user: "Bob Martinez",
      meta: {
        field: "category",
        previousValue: "Legal",
        newValue: "Licenses & Permits",
      },
    },
    {
      id: hid(104),
      type: "assigned",
      description: "Owner assigned: Bob Martinez",
      date: iso(200),
      user: "Alice Johnson",
    },
    {
      id: hid(105),
      type: "created",
      description: "Document created",
      date: iso(365),
      user: "Alice Johnson",
    },
  ],
  [iid(2)]: [
    {
      id: hid(201),
      type: "renewed",
      description: "Policy renewed — comprehensive coverage extended",
      date: iso(5),
      user: "Carol Smith",
      meta: { previousDate: past(180), newDate: d(12) },
    },
    {
      id: hid(202),
      type: "reminder_sent",
      description: "Automated reminder email sent (14 days before expiry)",
      date: iso(12),
      user: "Automated",
      meta: { days_before: 14, sent_to: "carol@company.com" },
    },
    {
      id: hid(203),
      type: "snoozed",
      description: "Reminder snoozed for 14 days",
      date: iso(20),
      user: "Carol Smith",
    },
    {
      id: hid(204),
      type: "edited",
      description: "Notes updated",
      date: iso(60),
      user: "Carol Smith",
      meta: {
        field: "notes",
        previousValue: "—",
        newValue: "Comprehensive coverage",
      },
    },
    {
      id: hid(205),
      type: "created",
      description: "Document created",
      date: iso(180),
      user: "Carol Smith",
    },
  ],
  [iid(3)]: [
    {
      id: hid(301),
      type: "assigned",
      description: "Owner assigned: David Lee",
      date: iso(2),
      user: "Bob Martinez",
    },
    {
      id: hid(302),
      type: "created",
      description: "Document created",
      date: iso(90),
      user: "Bob Martinez",
    },
  ],
  [iid(5)]: [
    {
      id: hid(501),
      type: "renewed",
      description: "Certificate renewed after bi-annual inspection",
      date: iso(1),
      user: "Alice Johnson",
      meta: { previousDate: past(184), newDate: d(4) },
    },
    {
      id: hid(502),
      type: "snoozed",
      description: "Reminder snoozed for 7 days",
      date: iso(15),
      user: "Alice Johnson",
    },
    {
      id: hid(503),
      type: "created",
      description: "Document created",
      date: iso(180),
      user: "Alice Johnson",
    },
  ],
  [iid(9)]: [
    {
      id: hid(901),
      type: "created",
      description: "Document created",
      date: iso(365),
      user: "Alice Johnson",
    },
  ],
  [iid(11)]: [
    {
      id: hid(1101),
      type: "renewed",
      description: "Lease renewed for 12 months",
      date: iso(30),
      user: "David Lee",
      meta: { previousDate: past(395), newDate: d(365) },
    },
    {
      id: hid(1102),
      type: "assigned",
      description: "Owner assigned: Emily Chen",
      date: iso(45),
      user: "David Lee",
    },
    {
      id: hid(1103),
      type: "edited",
      description: "Notes updated",
      date: iso(60),
      user: "David Lee",
      meta: {
        field: "notes",
        previousValue: "—",
        newValue: "Head office lease renewal next year",
      },
    },
    {
      id: hid(1104),
      type: "created",
      description: "Document created",
      date: iso(500),
      user: "Alice Johnson",
    },
  ],
};

// ─── Mock Auth ────────────────────────────────────────────────────────────────
export const MOCK_AUTH_USER = {
  id: uid(99),
  email: "demo@company.com",
  name: "Demo User",
};

export const MOCK_TOKEN = "mock-jwt-token-dev-only";
